import { redirect } from "react-router-dom";

import AppWrapper from "./components/cm/AppWrapper";
import SideBarMain from "./components/cm/SideBarMain/SideBar";
import DetailSectionContent from "./components/modules/dynamic-views/view-management/components/right-panel/DetailSectionContent";
import DetailSubTabLayout from "./components/modules/dynamic-views/view-management/components/right-panel/DetailSubTabLayout";
import DetailTabLayout from "./components/modules/dynamic-views/view-management/components/right-panel/DetailTabLayout";
import ViewManagement from "./components/modules/dynamic-views/view-management/ViewManagement";
import {
  buildRouteId,
  buildTabPath,
  getSubtabs,
  getViewTabs,
  hasSubtabs,
} from "./dynamicViewRouteHelpers";

const DYNAMIC_VIEW_NAMES = ["entity", "campaign", "lawfirm"];

function DynamicViewLayout() {
  return (
    <AppWrapper.WithSidebar sidebar={<SideBarMain />}>
      <ViewManagement />
    </AppWrapper.WithSidebar>
  );
}

const getContentElement = ({ tab, subtab }) => {
  const ContentComponent = subtab?.component ?? tab?.component;

  if (ContentComponent) {
    return <ContentComponent />;
  }

  return (
    <DetailSectionContent
      title={subtab?.name ?? tab?.name}
      description={subtab?.description ?? tab?.description}
    />
  );
};

const createViewHelpers = (withSessionRedirects, viewName) => {
  const tabs = getViewTabs(viewName);
  const guardedLoader = withSessionRedirects(
    async ({ params }) => ({ viewName, id: params.id }),
    { isLoginRoute: false }
  );
  const redirectLoader = (buildPath) =>
    withSessionRedirects(async ({ params }) => redirect(buildPath(params)), {
      isLoginRoute: false,
    });
  const routeId = (...parts) => buildRouteId(`${viewName}__param_id`, ...parts);

  return { tabs, guardedLoader, redirectLoader, routeId };
};

const createSubtabRoute = (helpers, viewName, tab, subtab) => ({
  id: helpers.routeId(tab.key, subtab.key),
  path: subtab.key,
  element: (
    <DetailSubTabLayout
      viewName={viewName}
      parentTab={tab.key}
      activeTab={subtab.key}
    />
  ),
  loader: helpers.guardedLoader,
  children: [
    {
      id: helpers.routeId(tab.key, subtab.key, "index"),
      index: true,
      element: getContentElement({ tab, subtab }),
      loader: helpers.guardedLoader,
    },
  ],
});

const createTabIndexRoute = (helpers, viewName, tab) =>
  hasSubtabs(tab)
    ? {
        id: helpers.routeId(tab.key, "index"),
        index: true,
        loader: helpers.redirectLoader((params) => buildTabPath(viewName, params.id, tab)),
      }
    : {
        id: helpers.routeId(tab.key, "index"),
        index: true,
        element: (
          <DetailSectionContent
            title={tab.name}
            description={tab.description ?? `${tab.name} content will appear here.`}
          />
        ),
        loader: helpers.guardedLoader,
      };

const createTabRoute = (helpers, viewName, tab) => ({
  id: helpers.routeId(tab.key),
  path: tab.key,
  element: <DetailTabLayout viewName={viewName} activeTab={tab.key} />,
  loader: helpers.guardedLoader,
  children: [
    createTabIndexRoute(helpers, viewName, tab),
    ...getSubtabs(tab).map((subtab) => createSubtabRoute(helpers, viewName, tab, subtab)),
  ],
});

const createDynamicViewRoute = (withSessionRedirects, viewName) => {
  const helpers = createViewHelpers(withSessionRedirects, viewName);
  const defaultTab = helpers.tabs[0];

  return {
    id: helpers.routeId(),
    path: `${viewName}/:id`,
    element: <DynamicViewLayout />,
    loader: helpers.guardedLoader,
    children: [
      {
        id: helpers.routeId("index"),
        index: true,
        loader: helpers.redirectLoader((params) => buildTabPath(viewName, params.id, defaultTab)),
      },
      ...helpers.tabs.map((tab) => createTabRoute(helpers, viewName, tab)),
    ],
  };
};

export const createDynamicViewRoutes = (withSessionRedirects) =>
  DYNAMIC_VIEW_NAMES.map((viewName) => createDynamicViewRoute(withSessionRedirects, viewName));
